import { IDLE_STATUS, LOADING_STATUS, SUCCESS_STATUS, FAILURE_STATUS } from '@utils/redux';
import { TRAIN_STEPS } from '@utils/hooks/machineLearning/params';
import { blue } from '@mui/material/colors';


const BLOCKLY_LOAD_START = 'BLOCKLY/LOAD_START';
const BLOCKLY_LOAD_FINISH = 'BLOCKLY/LOAD_FINISH';

const UPDATE_BLOCKLY = 'BLOCKLY/UPDATE';
const BLOCKLY_BUILD = 'BLOCKLY/BUILD';
const BLOCKLY_BUILD_PORT = 'BLOCKLY/BUILD_PORT';
const BLOCKLY_BUILD_MODAL_OPEN = 'BLOCKLY/BUILD_MODAL_OPEN';
const BLOCKLY_BUILD_MODAL_CLOSE = 'BLOCKLY/BUILD_MODAL_CLOSE';
const BLOCKLY_BUILD_PROGRESS = 'BLOCKLY/BUILD_PROGRESS';
const BLOCKLY_STOP_BUILD = 'BLOCKLY/STOP_BUILD';

const WEBSERIAL_OPEN = 'WEBSERIAL/OPEN';
const WEBSERIAL_CLOSE = 'WEBSERIAL/CLOSE';
const WEBSERIAL_CHANGE_STEP = 'WEBSERIAL/CHANGE_STEP';
const WEBSERIAL_CHANGE_STATE = 'WEBSERIAL/CHANGE_STATE';

const BLOCKLY_SET_ML_ACTIVE = 'MACHINE_LEARNING/SET_ACTIVE';
const BLOCKLY_SET_ML_TRAINING_STEP = 'MACHINE_LEARNING/SET_TRAINING_STEP';
const BLOCKLY_SET_ML_MODEL = 'MACHINE_LEARNING/SET_MODEL';
const BLOCKLY_SET_ML_MIN_SAMPLES_REQUIRED = 'MACHINE_LEARNING/SET_MIN_SAMPLES_REQUIRED';
const BLOCKLY_SET_ML_TRAINING_PARAMS = 'MACHINE_LEARNING/SET_TRAINING_PARAMS';
const BLOCKLY_SET_ML_MODEL_TRAINED = 'MACHINE_LEARNING/SET_MODEL_TRAINED';
const BLOCKLY_SET_ML_IS_TRAINING = 'MACHINE_LEARNING/SET_IS_TRAINING';
const BLOCKLY_SET_ML_IS_CLASSIFYING = 'MACHINE_LEARNING/SET_IS_CLASSIFYING';
const BLOCKLY_SET_ML_SAMPLES = 'MACHINE_LEARNING/SET_SAMPLES';
const BLOCKLY_SET_ML_PREDICTION = 'MACHINE_LEARNING/SET_PREDICTION';
const BLOCKLY_SET_ML_MODEL_JSON = 'MACHINE_LEARNING/SET_MODEL_JSON';
const BLOCKLY_SET_ML_MODEL_WEIGHTS = 'MACHINE_LEARNING/SET_MODEL_WEIGHTS';
const BLOCKLY_SET_ML_SAMPLES_MODAL_ACTIVE = 'MACHINE_LEARNING/SET_SAMPLES_MODAL_ACTIVE';
const BLOCKLY_DELETE_ML_TENSORS = 'MACHINE_LEARNING/DELETE_ML_TENSORS';

const BLOCKLY_SET_BLUETOOTH_STATE = 'BLOCKLY/SET_BLUETOOTH_STATE';


export const initialState = {
  project: {},
  loading: false,
  error: null,
  status: IDLE_STATUS,
  webSerialModal: {
    open: false,
    step: 0,
    connected: false,
    error: null,
  },
  build: {
    building: false,
    modalOpen: false,
    progress: 0,
    microbitForceDownload: false,
    compileFromQueueId: null,
    remoteProjectId: null,
    port: '',
  },
  machineLearning: {
    active: false,
    trainingStep: TRAIN_STEPS.TEACH,
    model: null,
    minSamplesRequired: {
      samples: 0,
      classes: 0,
    },
    trainingParams: {
      epochs: 0,
      learningRate: 0,
      batchSize: 0,
      fineTunningEpochs: 0,
      denseUnits: 0,
    },
    modelJson: null,
    modelWeights: null,
    samples: {},
    samplesModalActive: false,
    modelTrained: false,
    isTraining: false,
    isClassifying: false,
    prediction: {},
    bluetooth: {
      connected: false,
      error: null,
    },
  },
};

export default function blocklyReducer(state = initialState, action) {
  switch (action.type) {
    case BLOCKLY_LOAD_START: {
      return {
        ...state,
        loading: true,
      };
    }
    case BLOCKLY_LOAD_FINISH: {
      return {
        ...state,
        loading: false,
      };
    }
    case UPDATE_BLOCKLY: {
      const { project } = action.payload;
      return {
        ...state,
        project: { ...state.project, ...project },
        status: SUCCESS_STATUS,
      };
    }
    case WEBSERIAL_OPEN: {
      return {
        ...state,
        webSerialModal: { ...state.webSerialModal, open: true },
      };
    }
    case BLOCKLY_BUILD: {
      const { params } = action.payload;
      return {
        ...state,
        build: {
          ...state.build,
          ...params,
          building: true,
        },
      };
    }
    case BLOCKLY_BUILD_MODAL_OPEN: {
      return {
        ...state,
        build: {
          ...state.build,
          modalOpen: true,
        },
      };
    }
    case BLOCKLY_BUILD_MODAL_CLOSE: {
      return {
        ...state,
        build: {
          ...state.build,
          modalOpen: false,
        },
      };
    }
    case BLOCKLY_STOP_BUILD: {
      return {
        ...state,
        build: {
          ...state.build,
          building: false,
          microbitForceDownload: false,
        },
      };
    }
    case BLOCKLY_BUILD_PROGRESS: {
      const { progress } = action.payload;
      return {
        ...state,
        build: {
          ...state.build,
          progress,
        },
      };
    }
    case BLOCKLY_BUILD_PORT: {
      const { port } = action.payload;
      return {
        ...state,
        build: {
          ...state.build,
          port,
        },
      };
    }
    case WEBSERIAL_CLOSE: {
      return {
        ...state,
        webSerialModal: { ...state.webSerialModal, open: false },
      };
    }
    case WEBSERIAL_CHANGE_STEP: {
      const { step } = action.payload;
      return {
        ...state,
        webSerialModal: { ...state.webSerialModal, step },
      };
    }
    case WEBSERIAL_CHANGE_STATE: {
      const { connected, error } = action.payload;
      return {
        ...state,
        webSerialModal: { ...state.webSerialModal, connected, error },
      };
    }
    case BLOCKLY_SET_ML_ACTIVE: {
      const { active } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          active,
        },
      };
    }
    case BLOCKLY_SET_ML_SAMPLES_MODAL_ACTIVE: {
      const { active } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          samplesModalActive: active,
        },
      };
    }
    case BLOCKLY_SET_ML_TRAINING_STEP: {
      const { trainingStep } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          trainingStep,
        },
      };
    }
    case BLOCKLY_SET_ML_MODEL: {
      const { model } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          model,
        },
      };
    }
    case BLOCKLY_SET_ML_MIN_SAMPLES_REQUIRED: {
      const { minSamples } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          minSamplesRequired: minSamples,
        },
      };
    }
    case BLOCKLY_SET_ML_TRAINING_PARAMS: {
      const { trainingParams } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          trainingParams,
        },
      };
    }
    case BLOCKLY_SET_ML_MODEL_TRAINED: {
      const { modelTrained } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          modelTrained,
        },
      };
    }
    case BLOCKLY_SET_ML_IS_TRAINING: {
      const { isTraining } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          isTraining,
        },
      };
    }
    case BLOCKLY_SET_ML_IS_CLASSIFYING: {
      const { isClassifying } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          isClassifying,
        },
      };
    }
    case BLOCKLY_SET_ML_SAMPLES: {
      const { samples } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          samples,
        },
      };
    }
    case BLOCKLY_SET_ML_PREDICTION: {
      const { prediction } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          prediction,
        },
      };
    }
    case BLOCKLY_SET_ML_MODEL_JSON: {
      const { modelJson } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          modelJson,
        },
      };
    }
    case BLOCKLY_SET_ML_MODEL_WEIGHTS: {
      const { modelWeights } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          modelWeights,
        },
      };
    }
    case BLOCKLY_DELETE_ML_TENSORS: {
      const samples = { ...state.machineLearning.samples };
      Object.keys(samples).forEach((key) => {
        samples[key] = {
          ...samples[key],
          tensors: [],
        };
      });
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          samples,
        },
      };
    }
    case BLOCKLY_SET_BLUETOOTH_STATE: {
      const { connected, error } = action.payload;
      return {
        ...state,
        machineLearning: {
          ...state.machineLearning,
          bluetooth: {
            connected,
            error,
          },
        },
      };
    }
    default:
      return state;
  }
}

// selectors
export const selectBlockly = (state) => state.blockly;

// actions
export const startLoading = () => (dispatch) => {
  dispatch({
    type: BLOCKLY_LOAD_START,
    payload: {},
  });
};

export const finishLoading = () => (dispatch) => {
  dispatch({
    type: BLOCKLY_LOAD_FINISH,
    payload: {},
  });
};

export const updateBlockly = (project) => (dispatch) => {
  dispatch({
    type: UPDATE_BLOCKLY,
    payload: {
      project,
    },
  });
};

export const build = (params = {}) => (dispatch) => {
  const newParams = {};
  if (typeof params.compileFromQueueId !== 'undefined') newParams.compileFromQueueId = params.compileFromQueueId;
  if (typeof params.remoteProjectId !== 'undefined') newParams.remoteProjectId = params.remoteProjectId;
  if (typeof params.port !== 'undefined') newParams.port = params.port;
  if (typeof params.microbitForceDownload !== 'undefined') newParams.microbitForceDownload = params.microbitForceDownload;
  dispatch({
    type: BLOCKLY_BUILD,
    payload: {
      params: newParams,
    },
  });
};

export const openBuildProgressModal = () => (dispatch) => {
  dispatch({
    type: BLOCKLY_BUILD_MODAL_OPEN,
    payload: {},
  });
};

export const closeBuildProgressModal = () => (dispatch) => {
  dispatch({
    type: BLOCKLY_BUILD_MODAL_CLOSE,
    payload: {},
  });
};

export const stopBuild = () => (dispatch) => {
  dispatch({
    type: BLOCKLY_STOP_BUILD,
    payload: {},
  });
};

export const setBuildProgress = (progress) => (dispatch) => {
  dispatch({
    type: BLOCKLY_BUILD_PROGRESS,
    payload: { progress },
  });
};

export const setBuildPort = (port) => (dispatch) => {
  dispatch({
    type: BLOCKLY_BUILD_PORT,
    payload: { port },
  });
};

export const webSerialModalOpen = () => (dispatch) => {
  dispatch({
    type: WEBSERIAL_OPEN,
    payload: {},
  });
};

export const webSerialModalClose = () => (dispatch) => {
  dispatch({
    type: WEBSERIAL_CLOSE,
    payload: {},
  });
};

export const webSerialModalChangeStep = (step) => (dispatch) => {
  dispatch({
    type: WEBSERIAL_CHANGE_STEP,
    payload: { step },
  });
};

export const webSerialModalChangeState = (connected, error) => (dispatch) => {
  dispatch({
    type: WEBSERIAL_CHANGE_STATE,
    payload: { connected, error },
  });
};

export const setMLActive = (active) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_ACTIVE,
    payload: { active },
  });
};

export const setMLSamplesModalActive = (active) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_SAMPLES_MODAL_ACTIVE,
    payload: { active },
  });
};

export const setMLTrainingStep = (trainingStep) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_TRAINING_STEP,
    payload: { trainingStep },
  });
};

export const setMLModel = (model) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_MODEL,
    payload: { model },
  });
};

export const setMLMinSamplesRequired = (minSamples) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_MIN_SAMPLES_REQUIRED,
    payload: { minSamples },
  });
};

export const setMLTrainingParams = (trainingParams) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_TRAINING_PARAMS,
    payload: { trainingParams },
  });
};

export const setMLModelTrained = (modelTrained) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_MODEL_TRAINED,
    payload: { modelTrained },
  });
};

export const setMLIsTraining = (isTraining) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_IS_TRAINING,
    payload: { isTraining },
  });
};

export const setMLIsClassifying = (isClassifying) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_IS_CLASSIFYING,
    payload: { isClassifying },
  });
};

export const setMLSamples = (samples) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_SAMPLES,
    payload: { samples },
  });
};

export const setMLPrediction = (prediction) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_PREDICTION,
    payload: { prediction },
  });
};

export const setMLModelJson = (modelJson) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_MODEL_JSON,
    payload: { modelJson },
  });
};

export const setMLModelWeights = (modelWeights) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_ML_MODEL_WEIGHTS,
    payload: { modelWeights },
  });
}

export const deleteMLTensors = () => (dispatch) => {
  dispatch({
    type: BLOCKLY_DELETE_ML_TENSORS,
    payload: {},
  });
}

export const setBluetoothState = (connected, error) => (dispatch) => {
  dispatch({
    type: BLOCKLY_SET_BLUETOOTH_STATE,
    payload: { connected, error },
  });
}